import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { STORAGE_KEYS } from '../constants/storage-keys';

interface MeResponse {
  data: {
    user: { admin_role?: string | null; is_super_admin?: boolean };
    permissions: string[];
    is_super_admin: boolean;
  };
}

/** Sidebar order used to send an admin to the first page they can open. */
const LANDING_PAGES: { path: string; permission?: string; anyOf?: string[] }[] = [
  { path: '/dashboard', permission: 'dashboard.read' },
  { path: '/claims', permission: 'claims.read' },
  { path: '/pharmacies', permission: 'pharmacies.read' },
  { path: '/admins', permission: 'admins.read' },
  { path: '/roles', permission: 'roles.read' },
  { path: '/users', permission: 'users.read' },
  { path: '/profiles', permission: 'profiles.read' },
  { path: '/products', permission: 'products.read' },
  { path: '/hub/videos', permission: 'hub.videos.read' },
  { path: '/hub/articles', permission: 'hub.articles.read' },
  { path: '/hub/games', permission: 'hub.games.read' },
  { path: '/hub/badges', permission: 'hub.badges.read' },
  { path: '/rewards', permission: 'rewards.read' },
  { path: '/redemptions', permission: 'redemptions.read' },
  { path: '/tiers', permission: 'tiers.read' },
  { path: '/config/points', permission: 'config.points.read' },
  { path: '/config/streak', permission: 'config.streak.read' },
  { path: '/notifications/history', permission: 'notifications.read' },
  { path: '/notifications/send', permission: 'notifications.send' },
  { path: '/export', anyOf: ['export.claims', 'export.users', 'export.profiles', 'export.pharmacies', 'export.activities'] },
];

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpClient);
  private readonly permissionSet = signal<ReadonlySet<string>>(this.readStored());
  private readonly superAdmin = signal(localStorage.getItem(STORAGE_KEYS.IS_SUPER_ADMIN) === '1');
  private loadPromise: Promise<void> | null = null;
  private readonly onStorage = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) return;
    if (
      event.key !== null &&
      event.key !== STORAGE_KEYS.PERMISSIONS &&
      event.key !== STORAGE_KEYS.IS_SUPER_ADMIN &&
      event.key !== STORAGE_KEYS.ACCESS_TOKEN
    ) {
      return;
    }
    this.reloadFromStorage();
  };

  readonly isSuperAdmin = this.superAdmin.asReadonly();

  constructor() {
    window.addEventListener('storage', this.onStorage);
  }

  has(permission: string): boolean {
    if (this.superAdmin()) return true;
    return this.permissionSet().has(permission);
  }

  hasAny(permissions: string[]): boolean {
    if (this.superAdmin()) return true;
    const held = this.permissionSet();
    return permissions.some((permission) => held.has(permission));
  }

  apply(permissions: string[] | null | undefined, isSuperAdmin: boolean): void {
    const next = new Set(permissions ?? []);
    this.permissionSet.set(next);
    this.superAdmin.set(isSuperAdmin);
    localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify([...next]));
    localStorage.setItem(STORAGE_KEYS.IS_SUPER_ADMIN, isSuperAdmin ? '1' : '0');
    this.loadPromise = Promise.resolve();
  }

  clear(): void {
    this.permissionSet.set(new Set());
    this.superAdmin.set(false);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
    localStorage.removeItem(STORAGE_KEYS.IS_SUPER_ADMIN);
    this.loadPromise = null;
  }

  /** Loads the signed-in admin's permissions once per session. */
  ensureLoaded(): Promise<void> {
    if (!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = firstValueFrom(this.http.get<MeResponse>('api/v1/admin/auth/me'))
      .then((res) => this.apply(res.data.permissions, !!res.data.is_super_admin))
      .catch(() => undefined);
    return this.loadPromise;
  }

  firstAllowedPath(): string | null {
    for (const page of LANDING_PAGES) {
      if (page.permission && this.has(page.permission)) return page.path;
      if (page.anyOf && this.hasAny(page.anyOf)) return page.path;
    }
    return null;
  }

  private reloadFromStorage(): void {
    this.permissionSet.set(this.readStored());
    this.superAdmin.set(localStorage.getItem(STORAGE_KEYS.IS_SUPER_ADMIN) === '1');
    this.loadPromise = null;
  }

  private readStored(): Set<string> {
    const raw = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    if (!raw) return new Set();
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.filter((item): item is string => typeof item === 'string'));
    } catch {
      return new Set();
    }
  }
}
